import * as SliderPrimitive from "@radix-ui/react-slider";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProposalList } from "@/features/vision-360/proposal-management/list/context/use-proposal-list";

const MAX_POSITIONS = 200;

interface ProposalAdvancedFiltersProps {
  open: boolean;
  onClose: () => void;
}

export function ProposalAdvancedFilters({
  open,
  onClose,
}: ProposalAdvancedFiltersProps) {
  const { filters, updateFilters } = useProposalList();

  if (!open) return null;

  const minVal = filters.min_capacity === "" ? 0 : Number(filters.min_capacity);
  const maxVal =
    filters.max_capacity === "" ? MAX_POSITIONS : Number(filters.max_capacity);

  function handleSliderChange([min, max]: [number, number]) {
    updateFilters({
      min_capacity: min === 0 ? "" : String(min),
      max_capacity: max === MAX_POSITIONS ? "" : String(max),
    });
  }

  const posLabel =
    minVal === 0 && maxVal === MAX_POSITIONS
      ? "Qualquer"
      : maxVal === MAX_POSITIONS
        ? `${minVal} – 200+`
        : `${minVal} – ${maxVal}`;

  return (
    <div className="bg-card rounded-2xl border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">
          Filtros avançados
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-1.5 min-w-[160px] flex-1">
          <label className="text-xs font-medium text-muted-foreground">
            Segmento
          </label>
          <Select
            value={filters.industry || "all"}
            onValueChange={(v) =>
              updateFilters({ industry: v === "all" ? "" : v })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {[
                "Tecnologia",
                "Financeiro",
                "Criativo",
                "Saúde",
                "Educação",
                "Logística",
                "Varejo",
                "Serviços",
              ].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col min-w-[160px] flex-1">
          <label className="text-xs font-medium text-muted-foreground">
            Cidade
          </label>
          <div>
            <Input
              placeholder="Ex: São Paulo"
              value={filters.city}
              onChange={(e) => updateFilters({ city: e.target.value })}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5 min-w-[160px] flex-1">
          <label className="text-xs font-medium text-muted-foreground">
            Prazo do contrato
          </label>
          <Select
            value={filters.contract_months || "all"}
            onValueChange={(v) =>
              updateFilters({ contract_months: v === "all" ? "" : v })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="6">6 meses</SelectItem>
              <SelectItem value="12">12 meses</SelectItem>
              <SelectItem value="24">24 meses</SelectItem>
              <SelectItem value="36">36 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-3 min-w-[200px] flex-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">
              Posições
            </label>
            <span className="text-xs font-medium text-foreground tabular-nums">
              {posLabel}
            </span>
          </div>
          <div className="flex items-center h-10">
            <SliderPrimitive.Root
              min={0}
              max={MAX_POSITIONS}
              step={1}
              minStepsBetweenThumbs={1}
              value={[minVal, maxVal]}
              onValueChange={(vals) =>
                handleSliderChange(vals as [number, number])
              }
              className="relative flex w-full touch-none select-none items-center"
            >
              <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted">
                <SliderPrimitive.Range className="absolute h-full bg-foreground" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-foreground bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer" />
              <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-foreground bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer" />
            </SliderPrimitive.Root>
          </div>
        </div>

        <div className="flex gap-6">
          <label className="flex flex-col gap-1.5 cursor-pointer">
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              Créditos flexíveis
            </span>
            <div className="flex h-10 items-center">
              <Switch
                checked={filters.has_flexible_credits}
                onCheckedChange={(checked) =>
                  updateFilters({ has_flexible_credits: checked })
                }
              />
            </div>
          </label>

          <label className="flex flex-col gap-1.5 cursor-pointer">
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              License Fee
            </span>
            <div className="flex h-10 items-center">
              <Switch
                checked={filters.has_license_fee}
                onCheckedChange={(checked) =>
                  updateFilters({ has_license_fee: checked })
                }
              />
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}
